import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  
  const [status, setStatus] = useState("checking"); // checking, success, error
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    checkPaymentStatus();
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
      } else if (response.data.status === "expired") {
        setStatus("error");
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setAttempts(prev => prev + 1);
      setTimeout(checkPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="payment-success-page">
      <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
        {status === "checking" && (
          <div data-testid="payment-checking-status">
            <Loader2 className="w-16 h-16 animate-spin text-[#D4AF37] mx-auto mb-6" />
            <h1 className="text-4xl font-bold mb-4">Confirming Your Payment</h1>
            <p className="text-lg text-[#8C8C8C]">
              Please wait while we verify your payment...
            </p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="payment-success-status">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-4xl font-bold mb-4">Payment Successful!</h1>
            <p className="text-lg text-[#8C8C8C] mb-8">
              Your 20% deposit has been received. Your date is now officially secured.
            </p>
            <p className="text-[#8C8C8C] mb-8">
              You'll receive a confirmation email shortly with all the details. 
              We look forward to creating something beautiful for your special day.
            </p>
            <Button
              onClick={() => navigate("/")}
              data-testid="return-home-button"
              className="bg-[#D4AF37] hover:bg-[#b89530] px-8 py-4 rounded-full text-lg"
            >
              Return Home
            </Button>
          </div>
        )}

        {status === "error" && (
          <div data-testid="payment-error-status">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="text-4xl font-bold mb-4">Payment Issue</h1>
            <p className="text-lg text-[#8C8C8C] mb-8">
              We couldn't confirm your payment. This could be due to a timeout or cancelled transaction.
            </p>
            <p className="text-[#8C8C8C] mb-8">
              Please contact us if you believe this is an error, or try booking again.
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => navigate("/inquiry")}
                className="bg-[#D4AF37] hover:bg-[#b89530] px-8 py-4 rounded-full text-lg"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="px-8 py-4 rounded-full text-lg"
              >
                Return Home
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;